/**
 * Formatea un precio en formato español (punto para miles, coma para decimales)
 * @param {number|string} price - Precio a formatear
 * @returns {string} - Precio formateado
 */
export const formatPrice = (price) => {
  if (!price && price !== 0) return '';
  
  let num = price;
  if (typeof price === 'string') {
    num = parseFloat(price.replace(/\./g, '').replace(',', '.'));
  }
  
  if (isNaN(num)) return '';
  
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true
  }).format(num);
};

/**
 * Parsea un precio en formato español a número
 * @param {string} priceStr - Precio en formato español
 * @returns {number} - Precio como número
 */
export const parsePrice = (priceStr) => {
  if (!priceStr) return 0;
  if (typeof priceStr === 'number') return priceStr;
  
  // Eliminar puntos (separadores de miles) y reemplazar coma por punto
  return parseFloat(priceStr.replace(/\./g, '').replace(',', '.')) || 0;
};

/**
 * Determina si un color hexadecimal es claro (necesita texto oscuro)
 * @param {string} hexColor - Color en formato hex
 * @returns {boolean} - true si es claro
 */
export const isLightColor = (hexColor) => {
  if (!hexColor) return false;
  
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

/**
 * Meses del año para selects
 */
export const MONTHS = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

/**
 * Destinos disponibles
 */
export const DESTINATIONS = ['ANMAR', 'GAIKER', 'IVAMI', 'REGULATORY'];

/**
 * Extrae el año de una fecha en formato dd/mm/yyyy
 * @param {string} dateStr - Fecha en formato dd/mm/yyyy
 * @returns {string|null} - Año o null
 */
export const extractYear = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return parts[2];
  }
  return null;
};

/**
 * Extrae el mes de una fecha en formato dd/mm/yyyy
 * @param {string} dateStr - Fecha en formato dd/mm/yyyy
 * @returns {string|null} - Mes (01-12) o null
 */
export const extractMonth = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    return parts[1];
  }
  return null;
};

/**
 * Obtiene la etiqueta del tipo de pago
 * @param {string} type - Tipo de pago
 * @returns {string} - Etiqueta legible
 */
export const getPaymentTypeLabel = (type) => {
  switch (type) {
    case 'UNICO': return 'Hito Único';
    case 'DOS_PAGOS': return '2 Hitos';
    case 'TRES_PAGOS': return '3 Hitos';
    case 'CUATRO_PAGOS': return '4 Hitos';
    default: return type;
  }
};

/**
 * Obtiene el total de pagos a partir del tipo de pago
 * @param {string} paymentType - Tipo de pago (UNICO, DOS_PAGOS, CUATRO_PAGOS)
 * @returns {number} - Total de pagos
 */
export const getTotalPaymentsFromType = (paymentType) => {
  switch (paymentType) {
    case 'UNICO': return 1;
    case 'DOS_PAGOS': return 2;
    case 'TRES_PAGOS': return 3;
    case 'CUATRO_PAGOS': return 4;
    default: return 1;
  }
};

/**
 * Formatea el nombre de un hito de facturación
 * @param {number} paymentNumber - Número del hito (1, 2, 3, 4)
 * @param {number} totalPayments - Total de hitos
 * @returns {string} - Nombre formateado del hito
 */
export const formatPaymentName = (paymentNumber, totalPayments) => {
  if (totalPayments === 1) {
    return 'Hito Único';
  }
  return `Hito ${paymentNumber} de ${totalPayments}`;
};

/**
 * Extrae el año de una fecha en cualquier formato
 * @param {string} dateStr - Fecha en formato DD/MM/YYYY o YYYY-MM-DD
 * @returns {string|null} - Año como string (ej: "2026") o null si no se puede extraer
 */
export const extractYearFromDate = (dateStr) => {
  if (!dateStr) return null;
  
  const str = String(dateStr).trim();
  if (!str) return null;
  
  // Si tiene formato ISO (YYYY-MM-DD)
  if (str.includes('-') && str.length >= 10) {
    const parts = str.split('T')[0].split(' ')[0].split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return parts[0];
    }
  }
  
  // Si tiene formato DD/MM/YYYY
  if (str.includes('/')) {
    const parts = str.split(' ')[0].split('/');
    if (parts.length === 3 && parts[2].length === 4) {
      return parts[2];
    }
  }
  
  return null;
};

/**
 * Genera una lista de años disponibles para seleccionar
 * @param {string} baseYear - Año base (de fecha_entrada)
 * @param {number} yearsAhead - Cuántos años hacia adelante incluir (default: 5)
 * @returns {string[]} - Array de años como strings
 */
export const generateYearOptions = (baseYear, yearsAhead = 5) => {
  const base = parseInt(baseYear) || new Date().getFullYear();
  const years = [];
  for (let i = 0; i <= yearsAhead; i++) {
    years.push(String(base + i));
  }
  return years;
};

/**
 * Formatea una fecha para mostrar solo DD/MM/YYYY sin hora
 * Maneja múltiples formatos de entrada
 * @param {string} dateStr - Fecha en cualquier formato
 * @returns {string} - Fecha formateada o 'N/A'
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  const str = String(dateStr).trim();
  if (!str) return 'N/A';
  
  // Si tiene formato ISO (2026-01-12T00:00:00 o 2026-01-12 00:00:00)
  if (str.includes('-') && str.length >= 10) {
    const datePart = str.split('T')[0].split(' ')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      // Convertir de YYYY-MM-DD a DD/MM/YYYY
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
  }
  
  // Si ya tiene formato DD/MM/YYYY (posiblemente con hora)
  if (str.includes('/')) {
    // Quitar cualquier parte de hora si existe
    const datePart = str.split(' ')[0];
    return datePart;
  }
  
  // Devolver tal cual si no coincide con ningún formato conocido
  return str;
};
